import React, { useEffect, useState, useCallback } from "react";
import { Box, Heading, Text, Button, Image, HStack } from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import QRCode from "qrcode";

interface QRCodeDisplayProps {
  qrCode: string;
  registrationId: string;
  userId: string;
  onRegisterAnother: () => void;
}

export const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  qrCode,
  registrationId,
  userId,
  onRegisterAnother,
}) => {
  const [qrImageUrl, setQrImageUrl] = useState<string>("");

  const generateQRCode = useCallback(async () => {
    try {
      const qrDataUrl = await QRCode.toDataURL(userId, {
        width: 256,
        margin: 1,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });
      setQrImageUrl(qrDataUrl);
    } catch (err) {
      console.error("Error generating QR code:", err);
    }
  }, [userId]);

  useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  const handleDownload = () => {
    if (!qrImageUrl) return;

    try {
      const link = document.createElement("a");
      link.href = qrImageUrl;
      link.download = `qr-code-${userId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading QR code:", error);
    }
  };

  return (
    <Box
      textAlign="center"
      p={8}
      borderRadius="2xl"
      bg="rgba(0, 0, 0, 0.3)"
      backdropFilter="blur(20px)"
      border="1px solid rgba(255, 255, 255, 0.1)"
      boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.2)"
      transition="all 0.3s ease-in-out"
    >
      <Heading size="md" mb={6} fontWeight="normal" color="white">
        Registration QR Code
      </Heading>
      <Box
        p={6}
        bg="white"
        borderRadius="2xl"
        display="inline-block"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
      >
        {qrImageUrl ? (
          <Image
            src={qrImageUrl}
            alt="Registration QR Code"
            width="256px"
            height="256px"
            style={{
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <Box
            width="256px"
            height="256px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="gray.100"
            color="gray.500"
          >
            Generating QR Code...
          </Box>
        )}
      </Box>
      <Text mt={4} color="white" fontSize="md">
        Registration ID: {userId}
      </Text>
      <Text mt={2} color="whiteAlpha.800" fontSize="sm">
        We've sent your QR code to your registered email address. You can also
        save it directly from here for quick access.
      </Text>
      <HStack mt={6} spacing={4} justify="center">
        <Button
          onClick={handleDownload}
          bg="pink.400"
          color="white"
          fontSize="md"
          h="45px"
          leftIcon={<DownloadIcon />}
          _hover={{
            bg: "pink.500",
          }}
          _active={{
            bg: "pink.600",
          }}
          isDisabled={!qrImageUrl}
        >
          Save
        </Button>
        <Button
          onClick={onRegisterAnother}
          variant="outline"
          color="white"
          fontSize="md"
          h="45px"
          borderColor="whiteAlpha.400"
          _hover={{
            bg: "whiteAlpha.100",
          }}
          _active={{
            bg: "whiteAlpha.200",
          }}
        >
          Register New User
        </Button>
      </HStack>
    </Box>
  );
};
